import {AfterViewInit, Component, OnDestroy, OnInit} from '@angular/core';
import {Chart, ChartConfiguration, ChartType, registerables} from 'chart.js';
import {AppConfig, AppConfigService} from '../../../app/app-config.service';
import {
  AssetService,
  ContractAgreementService,
  ContractDefinitionService,
  ContractNegotiationService,
  PolicyService,
  TransferProcessService
} from '../../../mgmt-api-client';
import {forkJoin, of} from 'rxjs';
import {catchError} from 'rxjs/operators';

Chart.register(...registerables);

interface ConfigVariable {
  label: string;
  value: string;
}

@Component({
  selector: 'edc-demo-introduction',
  templateUrl: './introduction.component.html',
  styleUrls: ['./introduction.component.scss']
})
export class IntroductionComponent implements OnInit, AfterViewInit, OnDestroy {
  private incomingChart?: Chart;
  private outgoingChart?: Chart;
  managementApiUrl = '';

  dataOffersCount = 0;
  assetsCount = 0;
  policiesCount = 0;
  negotiationsCount = 0;
  contractAgreementsCount = 0;

  incomingSeries = [0, 0, 0];
  outgoingSeries = [0, 0, 0];
  incomingTotal = 0;
  outgoingTotal = 0;

  configVariables: ConfigVariable[] = [];
  configValuesByKey: Record<string, string> = {};

  constructor(
    private appConfigService: AppConfigService,
    private assetService: AssetService,
    private policyService: PolicyService,
    private contractDefinitionService: ContractDefinitionService,
    private contractAgreementService: ContractAgreementService,
    private contractNegotiationService: ContractNegotiationService,
    private transferProcessService: TransferProcessService
  ) {
    const config = this.appConfigService.getConfig();
    this.managementApiUrl = config?.managementApiUrl ?? '';
    this.configVariables = this.buildConfigVariables(config);
    this.configValuesByKey = this.buildConfigValuesByKey(config);
  }

  ngOnInit(): void {
    forkJoin({
      assets: this.assetService.requestAssets().pipe(catchError(() => of([]))),
      policies: this.policyService.queryAllPolicies().pipe(catchError(() => of([]))),
      definitions: this.contractDefinitionService.queryAllContractDefinitions().pipe(catchError(() => of([]))),
      agreements: this.contractAgreementService.queryAllAgreements().pipe(catchError(() => of([]))),
      negotiations: this.contractNegotiationService.queryNegotiations({} as any).pipe(catchError(() => of([]))),
      transferProcesses: this.transferProcessService.queryAllTransferProcesses().pipe(catchError(() => of([])))
    }).subscribe({
      next: ({assets, policies, definitions, agreements, negotiations, transferProcesses}) => {
        this.assetsCount = assets.length;
        this.policiesCount = policies.length;
        this.dataOffersCount = definitions.length;
        this.contractAgreementsCount = agreements.length;
        this.negotiationsCount = negotiations.length;

        this.applyTransferMetrics(transferProcesses);
        this.updateCharts();
        // If canvases are conditionally rendered, ensure charts initialize after DOM updates.
        setTimeout(() => this.updateCharts());
      },
      error: error => {
        console.error('Error loading dashboard metrics:', error);
      }
    });
  }

  ngAfterViewInit(): void {
    this.initializeChartsIfNeeded();
    this.updateCharts();
  }

  ngOnDestroy(): void {
    this.incomingChart?.destroy();
    this.outgoingChart?.destroy();
  }

  selectReadonlyField(event: FocusEvent): void {
    const input = event.target as HTMLInputElement | null;
    input?.select();
  }

  copyToClipboard(value: string): void {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(value).catch(() => {
        // Ignore clipboard failures to avoid interrupting dashboard usage.
      });
    }
  }

  getConfigValue(key: string): string {
    return this.configValuesByKey[key] ?? '';
  }

  private initializeChartsIfNeeded(): void {
    if (typeof document === 'undefined') {
      return;
    }

    if (!this.incomingChart) {
      const incomingCanvas = document.getElementById('incomingDonutChart') as HTMLCanvasElement | null;
      if (incomingCanvas) {
        this.incomingChart = new Chart(incomingCanvas, this.buildDonutConfig(this.incomingSeries));
      }
    }

    if (!this.outgoingChart) {
      const outgoingCanvas = document.getElementById('outgoingDonutChart') as HTMLCanvasElement | null;
      if (outgoingCanvas) {
        this.outgoingChart = new Chart(outgoingCanvas, this.buildDonutConfig(this.outgoingSeries));
      }
    }
  }

  private applyTransferMetrics(transferProcesses: any[]): void {
    const incomingProcesses = transferProcesses.filter(process => process?.type === 'CONSUMER');
    const outgoingProcesses = transferProcesses.filter(process => process?.type === 'PROVIDER');

    this.incomingSeries = this.buildTransferSeries(incomingProcesses);
    this.outgoingSeries = this.buildTransferSeries(outgoingProcesses);
    this.incomingTotal = this.incomingSeries.reduce((acc, value) => acc + value, 0);
    this.outgoingTotal = this.outgoingSeries.reduce((acc, value) => acc + value, 0);
  }

  private buildTransferSeries(processes: any[]): number[] {
    let completed = 0;
    let inProgress = 0;
    let error = 0;

    for (const process of processes) {
      const state = (process?.state ?? '').toUpperCase();
      if (this.isErrorState(state)) {
        error += 1;
      } else if (this.isCompletedState(state)) {
        completed += 1;
      } else {
        inProgress += 1;
      }
    }

    return [completed, inProgress, error];
  }

  private isCompletedState(state: string): boolean {
    return ['COMPLETED', 'DEPROVISIONED', 'ENDED'].includes(state);
  }

  private isErrorState(state: string): boolean {
    return ['ERROR', 'TERMINATED'].includes(state);
  }

  private updateCharts(): void {
    this.initializeChartsIfNeeded();

    if (this.incomingChart) {
      this.incomingChart.data.datasets[0].data = this.incomingSeries;
      this.incomingChart.update();
    }

    if (this.outgoingChart) {
      this.outgoingChart.data.datasets[0].data = this.outgoingSeries;
      this.outgoingChart.update();
    }
  }

  private buildDonutConfig(values: number[]): ChartConfiguration<ChartType> {
    return {
      type: 'doughnut',
      data: {
        labels: ['Completed', 'In Progress', 'Error'],
        datasets: [
          {
            data: values,
            backgroundColor: ['#15bc8c', '#5a9ef6', '#ef4c48'],
            borderWidth: 0,
            hoverOffset: 0
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: 650
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            enabled: true
          }
        }
      }
    };
  }

  private buildConfigVariables(config: AppConfig | undefined): ConfigVariable[] {
    if (!config) {
      return [];
    }

    return Object.entries(config)
      .filter(([key, value]) => key !== 'connectors' && value !== undefined && value !== null)
      .map(([key, value]) => ({
        label: this.toDisplayLabel(key),
        value: this.toDisplayValue(value)
      }));
  }

  private buildConfigValuesByKey(config: AppConfig | undefined): Record<string, string> {
    if (!config) {
      return {};
    }

    return Object.entries(config)
      .filter(([, value]) => value !== undefined && value !== null)
      .reduce((acc, [key, value]) => {
        acc[key] = this.toDisplayValue(value);
        return acc;
      }, {} as Record<string, string>);
  }

  private toDisplayLabel(key: string): string {
    return key
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/^./, firstLetter => firstLetter.toUpperCase());
  }

  private toDisplayValue(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    return JSON.stringify(value);
  }
}
